import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import {
  FileText,
  Download,
  Calendar,
  DollarSign,
  TrendingDown,
  ChevronRight,
} from 'lucide-react';

export function EmployeePayslip() {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  // Fetch latest payslip
  const { data: latestPayslip, isLoading: latestLoading } = useQuery({
    queryKey: ['employee', 'payslip', 'latest'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/payslip/latest');
      return response.data;
    },
  });

  // Fetch payslip history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['employee', 'payslip', 'history', selectedYear],
    queryFn: async () => {
      const response = await api.get('/banking/employee/payslip/history', {
        params: { year: selectedYear },
      });
      return response.data;
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatMonth = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      month: 'long',
      year: 'numeric',
    }).format(new Date(date));
  };

  const handleDownload = async (payslipId: string) => {
    try {
      const response = await api.get(`/banking/employee/payslip/${payslipId}/pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `holerite-${payslipId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleDownloadIncomeReport = async () => {
    try {
      const response = await api.get('/banking/employee/payslip/income-report', {
        params: { year: selectedYear },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `informe-rendimentos-${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year.toString(), label: year.toString() };
  });


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Holerite</h1>
          <p className="text-surface-400">Consulte seus contracheques</p>
        </div>
        <Button variant="secondary" onClick={handleDownloadIncomeReport}>
          <Download size={18} className="mr-2" />
          Informe de Rendimentos
        </Button>
      </div>

      {/* Latest Payslip */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Último Holerite</h2>

          {latestLoading ? (
            <div className="animate-pulse">
              <div className="h-6 w-48 bg-surface-700 rounded mb-4" />
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-4 rounded-lg bg-surface-800/50">
                    <div className="h-3 w-16 bg-surface-700 rounded mb-2" />
                    <div className="h-6 w-24 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            </div>
          ) : latestPayslip ? (
            <>
              <p className="text-surface-400 mb-4 capitalize">
                {formatMonth(latestPayslip.referenceDate)}
              </p>

              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign size={16} className="text-green-400" />
                    <span className="text-xs text-green-400">Proventos</span>
                  </div>
                  <p className="text-xl font-semibold text-white">
                    {formatCurrency(latestPayslip.grossAmount)}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown size={16} className="text-red-400" />
                    <span className="text-xs text-red-400">Descontos</span>
                  </div>
                  <p className="text-xl font-semibold text-white">
                    {formatCurrency(latestPayslip.totalDeductions)}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-primary-500/10 border border-primary-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign size={16} className="text-primary-400" />
                    <span className="text-xs text-primary-400">Líquido</span>
                  </div>
                  <p className="text-xl font-semibold text-white">
                    {formatCurrency(latestPayslip.netAmount)}
                  </p>
                </div>
              </div>

              {/* Earnings */}
              {latestPayslip.earnings?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-surface-300 mb-3">Proventos</h3>
                  <div className="space-y-2">
                    {latestPayslip.earnings.map((item: any, index: number) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50"
                      >
                        <span className="text-sm text-white">{item.description}</span>
                        <span className="text-sm font-medium text-green-400">
                          +{formatCurrency(item.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deductions */}
              {latestPayslip.deductions?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-surface-300 mb-3">Descontos</h3>
                  <div className="space-y-2">
                    {latestPayslip.deductions.map((item: any, index: number) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50"
                      >
                        <span className="text-sm text-white">{item.description}</span>
                        <span className="text-sm font-medium text-red-400">
                          -{formatCurrency(item.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                variant="primary"
                className="w-full"
                onClick={() => handleDownload(latestPayslip.id)}
              >
                <Download size={18} className="mr-2" />
                Baixar PDF
              </Button>
            </>
          ) : (
            <div className="text-center py-8">
              <FileText size={48} className="mx-auto text-surface-600 mb-3" />
              <p className="text-surface-400">Nenhum holerite disponível</p>
            </div>
          )}
        </div>
      </Card>

      {/* History */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Histórico</h2>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              options={yearOptions}
              className="w-32"
            />
          </div>

          {historyLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 animate-pulse">
                  <div className="w-10 h-10 rounded-lg bg-surface-700" />
                  <div className="flex-1">
                    <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                    <div className="h-3 w-24 bg-surface-700 rounded" />
                  </div>
                  <div className="h-5 w-20 bg-surface-700 rounded" />
                </div>
              ))}
            </div>
          ) : historyData?.payslips?.length > 0 ? (
            <div className="space-y-2">
              {historyData.payslips.map((payslip: any) => (
                <div
                  key={payslip.id}
                  className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors cursor-pointer"
                  onClick={() => handleDownload(payslip.id)}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                    <Calendar size={20} className="text-primary-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white capitalize">
                      {formatMonth(payslip.referenceDate)}
                    </p>
                    <p className="text-xs text-surface-400">
                      Líquido: {formatCurrency(payslip.netAmount)}
                    </p>
                  </div>
                  <ChevronRight size={20} className="text-surface-500" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar size={48} className="mx-auto text-surface-600 mb-3" />
              <p className="text-surface-400">Nenhum holerite em {selectedYear}</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

export default EmployeePayslip;
